import React from "react";
import { useTable, useGlobalFilter, usePagination } from "react-table";
import { Link } from "react-router-dom";
import { FiEdit, FiTrash, FiEye } from "react-icons/fi";
import { FaSpinner } from "react-icons/fa";

// Global search input
function GlobalFilter({ globalFilter, setGlobalFilter }) {
  return (
    <input
      type="text"
      value={globalFilter || ""}
      onChange={(e) => setGlobalFilter(e.target.value)}
      placeholder="Search..."
      className="p-2 border rounded mb-4 w-full shadow"
    />
  );
}

const DataTable = ({
  data,
  columns,
  loading,
  addLink,
  editPath,
  detailPath,
  deleteHandler,
  showAddButton = true,
  showEditButton = false,
  showDeleteButton = false,
  showViewButton = true,
  selectable = false,
  selectedItems = [],
  onSelectionChange,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 20 },
    },
    useGlobalFilter,
    usePagination
  );

  const { globalFilter, pageIndex, pageSize } = state;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = data.map((item) => item._id);
      onSelectionChange(allIds);
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectItem = (itemId) => {
    if (selectedItems.includes(itemId)) {
      onSelectionChange(selectedItems.filter((id) => id !== itemId));
    } else {
      onSelectionChange([...selectedItems, itemId]);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center py-20">
        <FaSpinner className="animate-spin text-4xl text-blue-500 mb-4" />
        <p className="text-lg text-gray-600">Loading data...</p>
        <p className="text-sm text-gray-400 mt-2">
          Please wait while we fetch the information
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white p-5 rounded-lg shadow-md">
      <GlobalFilter
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
      />

      {/* Horizontal scroll support */}
      <div className="overflow-x-auto">
        <table
          {...getTableProps()}
          className="min-w-full border rounded shadow text-sm"
        >
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {selectable && (
                  <th className="py-2 px-2 border-b bg-gray-100 font-semibold sticky top-0 z-10">
                    <input
                      type="checkbox"
                      checked={
                        selectedItems.length === data.length && data.length > 0
                      }
                      onChange={handleSelectAll}
                      className="rounded"
                    />
                  </th>
                )}
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps()}
                    className="py-2 px-2 border-b text-left bg-gray-100 font-semibold whitespace-nowrap sticky top-0 z-10"
                  >
                    {column.render("Header")}
                  </th>
                ))}

                {(showViewButton || showEditButton || showDeleteButton) && (
                  <th className="py-2 px-2 border-b bg-gray-100 font-semibold sticky top-0 z-10">
                    Actions
                  </th>
                )}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} className="hover:bg-gray-50">
                  {selectable && (
                    <td className="py-1 px-2 border-b">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(row.original._id)}
                        onChange={() => handleSelectItem(row.original._id)}
                        className="rounded"
                      />
                    </td>
                  )}
                  {row.cells.map((cell) => (
                    <td
                      {...cell.getCellProps()}
                      className="py-1 px-2 border-b break-all max-w-[180px] truncate"
                    >
                      {cell.render("Cell")}
                    </td>
                  ))}

                  {(showViewButton || showEditButton || showDeleteButton) && (
                    <td className="py-1 px-2 border-b whitespace-nowrap space-x-2">
                      {showViewButton && (
                        <Link
                          to={`${detailPath}/${row.original._id}`}
                          className="text-blue-500 hover:text-blue-700"
                        >
                          <FiEye />
                        </Link>
                      )}
                      {showEditButton && (
                        <Link
                          to={`${editPath}/${row.original._id}`}
                          className="text-yellow-500 hover:text-yellow-700"
                        >
                          <FiEdit />
                        </Link>
                      )}
                      {showDeleteButton && (
                        <button
                          onClick={() => deleteHandler(row.original._id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <FiTrash />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="flex flex-wrap justify-between mt-4 items-center gap-2">
        <div className="flex gap-2">
          <button
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            className="bg-gray-300 px-2 py-1 rounded disabled:opacity-50 hover:bg-gray-400 transition"
          >
            Previous
          </button>
          <button
            onClick={() => nextPage()}
            disabled={!canNextPage}
            className="bg-gray-300 px-2 py-1 rounded disabled:opacity-50 hover:bg-gray-400 transition"
          >
            Next
          </button>
        </div>

        <span>
          Page{" "}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>
        </span>

        <span className="flex items-center gap-1">
          | Go to page:
          <input
            type="number"
            defaultValue={pageIndex + 1}
            onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0;
              gotoPage(page);
            }}
            className="border p-1 rounded w-14"
          />
        </span>

        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className="border p-1 rounded"
        >
          {[5, 10, 20, 30, 40, 50].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default DataTable;
